// Import all course data from unified source
import { getAllCoursesUnified } from '@/utils/allCourses';
import { normalizeQuizContent } from '@/utils/quizNormalizer';

// Type-specific imports for categorization
import { SQL_COURSES } from '@/data/sql';
import { SQL_PHASE1_COURSES } from '@/data/sql/phase1';
import { PYTHON_COURSES } from '@/data/python';
import { AI_COURSES } from '@/data/ai/courses';
import { JAVA_COURSES } from '@/data/java';
import { DOTNET_COURSES } from '@/data/dotnet';
import { CSHARP_COURSES } from '@/data/csharp';
import { AZURE_COURSES } from '@/data/azure';
import { AWS_COURSES } from '@/data/aws';
import { GCP_COURSES } from '@/data/gcp';
import { ENTERPRISE_TRAINING_COURSES } from '@/data/enterprise-training';
import { PROFESSIONAL_DEVELOPMENT_COURSES } from '@/data/professional-development';
import { JAVASCRIPT_COURSES } from '@/data/javascript';
import { DEVOPS_COURSES } from '@/data/devops';
import { BACKEND_COURSES } from '@/data/backend';
import { SECURITY_COURSES } from '@/data/security';
import { MOBILE_COURSES } from '@/data/mobile';
import { DATA_SCIENCE_COURSES } from '@/data/data-science';
import { DATABASE_COURSES } from '@/data/database';
import { BLOCKCHAIN_COURSES } from '@/data/blockchain';
import { RPA_COURSES } from '@/data/rpa';
import { GRAPHICS_COURSES } from '@/data/graphics';
import { DATASCIENCE_COURSES } from '@/data/datascience';
import { DATAENGINEERING_COURSES } from '@/data/dataengineering';
import { ETL_COURSES } from '@/data/etl';
import { APPSEC_COURSES } from '@/data/appsec';
import { INFRASTRUCTURE_COURSES } from '@/data/infrastructure';
import { FRONTEND_ADVANCED_COURSES } from '@/data/frontend-advanced';
import { TESTING_MODERN_COURSES } from '@/data/testing-modern';
import { SELENIUM_COURSES } from '@/data/testing';
import { WEB3_DEVELOPMENT_COURSES } from '@/data/web3-development';
import { SERVERLESS_EDGE_COURSES } from '@/data/serverless-edge';
import { POWERBI_COURSES } from '@/data/powerbi';
import { TABLEAU_COURSES } from '@/data/tableau';
import { UNITY_GAME_DEVELOPMENT, UNREAL_GAME_DEVELOPMENT, AR_VR_DEVELOPMENT, DESKTOP_APPLICATION_DEVELOPMENT } from '@/data/game-development';
import { UI_UX_COURSES } from '@/data/ui-ux-design';
import { GRAPHICS_PROGRAMMING_COURSES } from '@/data/graphics-programming';
import { RUST_COURSES } from '@/data/rust';
import { MODERN_FRONTEND_COURSES } from '@/data/modern-frontend';
import { KUBERNETES_MASTERY_COURSES } from '@/data/kubernetes-mastery';
import { EDGE_COMPUTING_COURSES } from '@/data/edge-computing';
import { CROSS_PLATFORM_COURSES } from '@/data/cross-platform';
import { GREEN_TECH_COURSES } from '@/data/green-tech';
import { NO_CODE_COURSES } from '@/data/no-code';
import { ADVANCED_API_COURSES } from '@/data/advanced-api';
import { ML_COURSES } from '@/data/ml';
import { getAllCourses } from '@/data/courses';

export interface QuizAuditResult {
  courseId: string;
  courseTitle: string;
  courseType: string;
  totalModules: number;
  modulesWithQuizzes: number;
  modulesWithoutQuizzes: string[];
  quizQualityScore: number;
  issues: string[];
  recommendations: string[];
}

export interface ComprehensiveQuizAuditReport {
  totalCourses: number;
  totalModules: number;
  modulesWithQuizzes: number;
  modulesWithoutQuizzes: number;
  overallQualityScore: number;
  courseResults: QuizAuditResult[];
  criticalIssues: string[];
  globalRecommendations: string[];
  courseTypeBreakdown: Record<string, {
    coursesCount: number;
    quizCoverage: number;
    averageQuality: number;
  }>;
}

export class ComprehensiveQuizAuditor {
  private allCourses = [
    // Programming Languages
    { courses: getAllCourses(), type: 'react' },
    { courses: PYTHON_COURSES, type: 'python' },
    { courses: JAVASCRIPT_COURSES, type: 'javascript' },
    { courses: JAVA_COURSES, type: 'java' },
    { courses: RUST_COURSES, type: 'rust' },
    { courses: CSHARP_COURSES, type: 'csharp' },
    { courses: DOTNET_COURSES, type: 'dotnet' },
    
    // Database & Data
    { courses: SQL_COURSES, type: 'sql' },
    { courses: SQL_PHASE1_COURSES, type: 'sql-phase1' },
    { courses: DATABASE_COURSES, type: 'database' },
    { courses: DATA_SCIENCE_COURSES, type: 'data-science' },
    { courses: DATASCIENCE_COURSES, type: 'datascience' },
    { courses: DATAENGINEERING_COURSES, type: 'data-engineering' },
    { courses: ETL_COURSES, type: 'etl' },
    
    // Cloud Platforms
    { courses: AWS_COURSES, type: 'aws' },
    { courses: AZURE_COURSES, type: 'azure' },
    { courses: GCP_COURSES, type: 'gcp' },
    
    // AI/ML
    { courses: AI_COURSES, type: 'ai' },
    { courses: ML_COURSES, type: 'ml' },
    
    // DevOps & Infrastructure
    { courses: DEVOPS_COURSES, type: 'devops' },
    { courses: INFRASTRUCTURE_COURSES, type: 'infrastructure' },
    { courses: KUBERNETES_MASTERY_COURSES, type: 'kubernetes' },
    { courses: SERVERLESS_EDGE_COURSES, type: 'serverless-edge' },
    { courses: EDGE_COMPUTING_COURSES, type: 'edge-computing' },
    
    // Backend & Security
    { courses: BACKEND_COURSES, type: 'backend' },
    { courses: SECURITY_COURSES, type: 'security' },
    { courses: APPSEC_COURSES, type: 'appsec' },
    
    // Frontend & Mobile
    { courses: MOBILE_COURSES, type: 'mobile' },
    { courses: FRONTEND_ADVANCED_COURSES, type: 'frontend-advanced' },
    { courses: MODERN_FRONTEND_COURSES, type: 'modern-frontend' },
    { courses: CROSS_PLATFORM_COURSES, type: 'cross-platform' },
    
    // Testing & Quality
    { courses: TESTING_MODERN_COURSES, type: 'testing-modern' },
    { courses: SELENIUM_COURSES, type: 'selenium' },
    
    // Emerging Tech
    { courses: BLOCKCHAIN_COURSES, type: 'blockchain' },
    { courses: WEB3_DEVELOPMENT_COURSES, type: 'web3' },
    { courses: RPA_COURSES, type: 'rpa' },
    { courses: NO_CODE_COURSES, type: 'no-code' },
    { courses: GREEN_TECH_COURSES, type: 'green-tech' },
    
    // Data Visualization & BI
    { courses: POWERBI_COURSES, type: 'powerbi' },
    { courses: TABLEAU_COURSES, type: 'tableau' },
    
    // Game Development & Graphics
    { courses: [UNITY_GAME_DEVELOPMENT], type: 'unity' },
    { courses: [UNREAL_GAME_DEVELOPMENT], type: 'unreal' },
    { courses: [AR_VR_DEVELOPMENT], type: 'ar-vr' },
    { courses: [DESKTOP_APPLICATION_DEVELOPMENT], type: 'desktop' },
    { courses: GRAPHICS_COURSES, type: 'graphics' },
    { courses: GRAPHICS_PROGRAMMING_COURSES, type: 'graphics-programming' },
    
    // Design & UX
    { courses: UI_UX_COURSES, type: 'ui-ux' },
    
    // APIs & Advanced
    { courses: ADVANCED_API_COURSES, type: 'advanced-api' },
    
    // Professional Development
    { courses: ENTERPRISE_TRAINING_COURSES, type: 'enterprise-training' },
    { courses: PROFESSIONAL_DEVELOPMENT_COURSES, type: 'professional-development' }
  ];

  public auditAllQuizzes(): ComprehensiveQuizAuditReport {
    console.log('🔍 Starting comprehensive quiz audit...');
    
    const courseResults: QuizAuditResult[] = [];
    let totalModules = 0;
    let modulesWithQuizzes = 0;
    const criticalIssues: string[] = [];
    const courseTypeBreakdown: Record<string, any> = {};

    // Audit each course set
    for (const courseSet of this.allCourses) {
      for (const course of courseSet.courses) {
        const result = this.auditCourse(course, courseSet.type);
        courseResults.push(result);
        
        totalModules += result.totalModules;
        modulesWithQuizzes += result.modulesWithQuizzes;
        
        // Track critical issues
        if (result.modulesWithoutQuizzes.length > 0) {
          criticalIssues.push(`${course.id}: ${result.modulesWithoutQuizzes.length} modules missing quizzes`);
        }
        
        // Update course type breakdown
        if (!courseTypeBreakdown[courseSet.type]) {
          courseTypeBreakdown[courseSet.type] = {
            coursesCount: 0,
            quizCoverage: 0,
            averageQuality: 0
          };
        }
        
        courseTypeBreakdown[courseSet.type].coursesCount++;
        courseTypeBreakdown[courseSet.type].quizCoverage += (result.modulesWithQuizzes / result.totalModules) * 100;
        courseTypeBreakdown[courseSet.type].averageQuality += result.quizQualityScore;
      }
    }

    // Calculate averages for course type breakdown
    Object.keys(courseTypeBreakdown).forEach(type => {
      const breakdown = courseTypeBreakdown[type];
      breakdown.quizCoverage = breakdown.quizCoverage / breakdown.coursesCount;
      breakdown.averageQuality = breakdown.averageQuality / breakdown.coursesCount;
    });

    const overallQualityScore = courseResults.reduce((sum, result) => sum + result.quizQualityScore, 0) / courseResults.length;
    const globalRecommendations = this.generateGlobalRecommendations(courseResults, courseTypeBreakdown);

    return {
      totalCourses: courseResults.length,
      totalModules,
      modulesWithQuizzes,
      modulesWithoutQuizzes: totalModules - modulesWithQuizzes,
      overallQualityScore,
      courseResults,
      criticalIssues,
      globalRecommendations,
      courseTypeBreakdown
    };
  }

  private auditCourse(course: any, courseType: string): QuizAuditResult {
    const modulesWithoutQuizzes: string[] = [];
    const issues: string[] = [];
    const recommendations: string[] = [];
    let totalQualityScore = 0;
    let modulesWithQuizzes = 0;

    for (const module of course.modules || []) {
      const quizContent = normalizeQuizContent(module);
      
      if (!quizContent || !quizContent.questions || quizContent.questions.length === 0) {
        modulesWithoutQuizzes.push(module.id);
        issues.push(`Module ${module.id}: No quiz content`);
      } else {
        modulesWithQuizzes++;
        const moduleQuality = this.assessModuleQuizQuality(quizContent, courseType);
        totalQualityScore += moduleQuality.score;
        
        if (moduleQuality.issues.length > 0) {
          issues.push(`Module ${module.id}: ${moduleQuality.issues.join(', ')}`);
        }
      }
    }

    const averageQuality = modulesWithQuizzes > 0 ? totalQualityScore / modulesWithQuizzes : 0;
    
    // Generate recommendations
    if (modulesWithoutQuizzes.length > 0) {
      recommendations.push(`Add quizzes to ${modulesWithoutQuizzes.length} modules`);
    }
    if (averageQuality < 70) {
      recommendations.push('Improve quiz quality with better questions and explanations');
    }

    return {
      courseId: course.id,
      courseTitle: course.title,
      courseType,
      totalModules: course.modules?.length || 0,
      modulesWithQuizzes,
      modulesWithoutQuizzes,
      quizQualityScore: averageQuality,
      issues,
      recommendations
    };
  }

  private assessModuleQuizQuality(quizContent: any, courseType: string): { score: number; issues: string[] } {
    const issues: string[] = [];
    let score = 100;

    // Check question count
    if (quizContent.questions.length < 3) {
      issues.push('Too few questions (minimum 3 recommended)');
      score -= 20;
    }

    // Check for explanations
    const questionsWithExplanations = quizContent.questions.filter((q: any) => q.explanation);
    if (questionsWithExplanations.length === 0) {
      issues.push('No explanations provided');
      score -= 30;
    } else if (questionsWithExplanations.length < quizContent.questions.length * 0.5) {
      issues.push('Less than 50% of questions have explanations');
      score -= 15;
    }

    // Check for difficulty levels
    const difficulties = quizContent.questions.map((q: any) => q.difficulty).filter(Boolean);
    if (difficulties.length === 0) {
      issues.push('No difficulty levels specified');
      score -= 15;
    }

    // Check course-specific content
    const hasRelevantContent = this.checkCourseRelevantContent(quizContent.questions, courseType);
    if (!hasRelevantContent) {
      issues.push(`Questions don't appear relevant to ${courseType} course`);
      score -= 20;
    }

    return { score: Math.max(0, score), issues };
  }

  private checkCourseRelevantContent(questions: any[], courseType: string): boolean {
    const relevantTerms: Record<string, string[]> = {
      // Programming Languages
      react: ['component', 'jsx', 'props', 'state', 'hook', 'react', 'virtual dom'],
      python: ['python', 'function', 'class', 'import', 'def', 'list', 'dictionary'],
      javascript: ['javascript', 'function', 'variable', 'array', 'object', 'callback'],
      java: ['java', 'class', 'interface', 'spring', 'maven', 'jvm', 'inheritance'],
      rust: ['ownership', 'borrowing', 'cargo', 'trait', 'lifetime', 'memory safety'],
      csharp: ['c#', 'class', '.net', 'linq', 'async', 'namespace'],
      dotnet: ['.net', 'framework', 'core', 'mvc', 'entity', 'razor'],
      
      // Database & Data
      sql: ['database', 'table', 'query', 'select', 'join', 'sql', 'index'],
      'sql-phase1': ['database', 'table', 'query', 'select', 'join', 'sql'],
      database: ['database', 'schema', 'normalization', 'acid', 'transaction'],
      'data-science': ['data science', 'analysis', 'statistics', 'visualization', 'model'],
      datascience: ['data science', 'analysis', 'statistics', 'pandas', 'numpy'],
      'data-engineering': ['pipeline', 'etl', 'data warehouse', 'spark', 'airflow'],
      etl: ['extract', 'transform', 'load', 'pipeline', 'integration', 'batch'],
      
      // Cloud Platforms
      aws: ['ec2', 's3', 'lambda', 'cloudformation', 'vpc', 'iam', 'aws'],
      azure: ['azure', 'resource group', 'vm', 'app service', 'cosmos', 'subscription'],
      gcp: ['google cloud', 'compute engine', 'cloud storage', 'bigquery', 'gke'],
      
      // AI/ML
      ai: ['artificial intelligence', 'machine learning', 'neural network', 'algorithm', 'ai'],
      ml: ['machine learning', 'model', 'training', 'prediction', 'algorithm', 'regression'],
      
      // DevOps & Infrastructure
      devops: ['ci/cd', 'pipeline', 'automation', 'deployment', 'jenkins', 'docker'],
      infrastructure: ['infrastructure', 'terraform', 'ansible', 'provisioning', 'iac'],
      kubernetes: ['pod', 'deployment', 'service', 'kubectl', 'helm', 'container'],
      'serverless-edge': ['serverless', 'edge', 'cdn', 'lambda', 'function', 'cloudflare'],
      'edge-computing': ['edge', 'latency', 'distributed', 'iot', 'caching'],
      
      // Backend & Security
      backend: ['api', 'server', 'endpoint', 'middleware', 'database', 'authentication'],
      security: ['security', 'encryption', 'authentication', 'vulnerability', 'threat'],
      appsec: ['application security', 'owasp', 'xss', 'sql injection', 'csrf'],
      
      // Frontend & Mobile
      mobile: ['mobile', 'app', 'ios', 'android', 'react native', 'flutter'],
      'frontend-advanced': ['frontend', 'performance', 'optimization', 'webpack', 'bundler'],
      'modern-frontend': ['frontend', 'component', 'state management', 'routing', 'hooks'],
      'cross-platform': ['cross platform', 'flutter', 'react native', 'xamarin', 'cordova'],
      
      // Testing & Quality
      'testing-modern': ['testing', 'unit test', 'integration', 'jest', 'cypress'],
      selenium: ['selenium', 'automation', 'webdriver', 'test', 'browser'],
      
      // Emerging Tech
      blockchain: ['blockchain', 'smart contract', 'ethereum', 'solidity', 'cryptocurrency'],
      web3: ['web3', 'decentralized', 'dapp', 'wallet', 'blockchain'],
      rpa: ['rpa', 'automation', 'robot', 'workflow', 'uipath'],
      'no-code': ['no code', 'low code', 'visual', 'builder', 'automation'],
      'green-tech': ['sustainability', 'green', 'energy', 'carbon', 'renewable'],
      
      // Data Visualization & BI
      powerbi: ['power bi', 'dax', 'dashboard', 'visualization', 'report'],
      tableau: ['tableau', 'visualization', 'dashboard', 'analytics', 'chart'],
      
      // Game Development & Graphics
      unity: ['unity', 'game engine', 'gameobject', 'prefab', 'scene'],
      unreal: ['unreal', 'blueprint', 'ue4', 'ue5', 'actor', 'material'],
      'ar-vr': ['ar', 'vr', 'virtual reality', 'augmented reality', 'headset'],
      desktop: ['desktop', 'gui', 'window', 'application', 'electron'],
      graphics: ['graphics', '3d', 'rendering', 'shader', 'texture'],
      'graphics-programming': ['graphics', 'opengl', 'directx', 'vulkan', 'shader'],
      
      // Design & UX
      'ui-ux': ['ui', 'ux', 'design', 'wireframe', 'prototype', 'figma'],
      
      // APIs & Advanced
      'advanced-api': ['api', 'rest', 'graphql', 'endpoint', 'microservice'],
      
      // Professional Development
      'enterprise-training': ['enterprise', 'business', 'training', 'corporate'],
      'professional-development': ['professional', 'career', 'leadership', 'communication']
    };

    const terms = relevantTerms[courseType] || [];
    if (terms.length === 0) return true; // If no specific terms defined, assume relevant

    const allText = questions.map(q => `${q.question} ${q.options?.join(' ')}`).join(' ').toLowerCase();
    return terms.some(term => allText.includes(term.toLowerCase()));
  }

  private generateGlobalRecommendations(
    courseResults: QuizAuditResult[], 
    courseTypeBreakdown: Record<string, any>
  ): string[] {
    const recommendations: string[] = [];
    
    // Overall coverage recommendations
    const overallCoverage = courseResults.reduce((sum, result) => 
      sum + (result.modulesWithQuizzes / result.totalModules), 0) / courseResults.length;
    
    if (overallCoverage < 0.8) {
      recommendations.push('URGENT: Less than 80% quiz coverage across all courses');
    }

    // Course type specific recommendations
    Object.entries(courseTypeBreakdown).forEach(([type, breakdown]: [string, any]) => {
      if (breakdown.quizCoverage < 70) {
        recommendations.push(`${type.toUpperCase()}: Low quiz coverage (${breakdown.quizCoverage.toFixed(1)}%)`);
      }
      if (breakdown.averageQuality < 60) {
        recommendations.push(`${type.toUpperCase()}: Poor quiz quality (${breakdown.averageQuality.toFixed(1)}%)`);
      }
    });

    // General recommendations
    recommendations.push('Implement standardized quiz templates for each course type');
    recommendations.push('Add automated quiz quality validation to prevent regressions');

    return recommendations;
  }

  public generateDetailedReport(): string {
    const audit = this.auditAllQuizzes();
    
    let report = '📊 COMPREHENSIVE QUIZ AUDIT REPORT\n';
    report += '=' .repeat(50) + '\n\n';
    
    report += '📈 OVERVIEW STATISTICS:\n';
    report += `• Total Courses: ${audit.totalCourses}\n`;
    report += `• Total Modules: ${audit.totalModules}\n`;
    report += `• Modules with Quizzes: ${audit.modulesWithQuizzes} (${((audit.modulesWithQuizzes / audit.totalModules) * 100).toFixed(1)}%)\n`;
    report += `• Modules without Quizzes: ${audit.modulesWithoutQuizzes}\n`;
    report += `• Overall Quality Score: ${audit.overallQualityScore.toFixed(1)}%\n\n`;

    report += '🎯 COURSE TYPE BREAKDOWN:\n';
    Object.entries(audit.courseTypeBreakdown).forEach(([type, breakdown]: [string, any]) => {
      report += `• ${type.toUpperCase()}:\n`;
      report += `  - Courses: ${breakdown.coursesCount}\n`;
      report += `  - Quiz Coverage: ${breakdown.quizCoverage.toFixed(1)}%\n`;
      report += `  - Average Quality: ${breakdown.averageQuality.toFixed(1)}%\n\n`;
    });

    report += '🚨 CRITICAL ISSUES:\n';
    audit.criticalIssues.forEach(issue => {
      report += `• ${issue}\n`;
    });
    report += '\n';

    report += '💡 GLOBAL RECOMMENDATIONS:\n';
    audit.globalRecommendations.forEach(rec => {
      report += `• ${rec}\n`;
    });
    report += '\n';

    report += '📋 DETAILED COURSE RESULTS:\n';
    audit.courseResults.forEach(result => {
      report += `\n${result.courseId} (${result.courseType}):\n`;
      report += `  📊 ${result.modulesWithQuizzes}/${result.totalModules} modules have quizzes (${((result.modulesWithQuizzes / result.totalModules) * 100).toFixed(1)}%)\n`;
      report += `  🎯 Quality Score: ${result.quizQualityScore.toFixed(1)}%\n`;
      
      if (result.modulesWithoutQuizzes.length > 0) {
        report += `  ❌ Missing Quizzes: ${result.modulesWithoutQuizzes.join(', ')}\n`;
      }
      
      if (result.issues.length > 0) {
        report += `  ⚠️  Issues: ${result.issues.join('; ')}\n`;
      }
      
      if (result.recommendations.length > 0) {
        report += `  💡 Recommendations: ${result.recommendations.join('; ')}\n`;
      }
    });

    return report;
  }
}

export const comprehensiveQuizAuditor = new ComprehensiveQuizAuditor();
